/**
 * Loop Subscriptions — Ajout d'un preset (fixed) bundle au panier Shopify
 * Fichier : assets/loop-preset-bundle.js
 *
 * Flow (doc Loop Storefront API, aucun token requis) :
 *   1. POST /storefront/{version}/bundle/{bundleId}/transaction  → data.txnId
 *   2. POST /cart/add.js avec _bundleId = txnId sur chaque ligne
 *   3. Attribut de panier _loopBundleDiscountAttributes = { [txnId]: {...} }
 *
 * Utilisation :
 *   LoopPresetBundle.add({
 *     bundleId: 'ID_DU_BUNDLE_LOOP',
 *     bundleName: 'All-Day Hydration Kit',
 *     sellingPlanId: 7787806969,          // null ou absent = achat unique
 *     variants: [
 *       { id: 47474019631353, quantity: 1 },
 *       { id: 46740565065977, quantity: 1 },
 *     ],
 *   });
 */
(function () {
  'use strict';

  const DEBUG = true; // false en production
  const LOOP_API = 'https://api.loopsubscriptions.com/storefront';
  const LOOP_API_VERSION = '2026-04';

  // Propriété de ligne qui reçoit le txnId.
  // Ton panier actuel (widget Loop) utilise _bundleId. La doc "Post-transaction setup"
  // mentionne aussi _loopBundleTxnId pour les preset bundles : ajoute-le ici au besoin.
  const TXN_PROPERTY_KEYS = ['_bundleId'];

  const log = (...args) => DEBUG && console.log('[LoopBundle]', ...args);
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const detailsCache = {};

  /* ------------------------------------------------------------------ */
  /* Détails du bundle (pour trouver boxSizeId / discountId au besoin)   */
  /* ------------------------------------------------------------------ */
  async function getBundleDetails(bundleId) {
    if (detailsCache[bundleId]) return detailsCache[bundleId];

    const res = await fetch(`${LOOP_API}/${LOOP_API_VERSION}/bundle/${bundleId}`);
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.message || 'Bundle Loop introuvable');

    log('Détails du bundle :', json.data);
    detailsCache[bundleId] = json.data;
    return json.data;
  }

  function pickDiscount(details, isSubscription) {
    const discounts = details.discounts || [];
    const wanted = isSubscription ? 'SUBSCRIPTION' : 'ONE_TIME';
    return (
      discounts.find((d) => d.purchaseType === wanted) ||
      discounts.find((d) => d.purchaseType === 'ALL') ||
      discounts.find((d) => isSubscription ? d.purchaseType !== 'ONE_TIME' : d.purchaseType !== 'SUBSCRIPTION') ||
      null
    );
  }

  /* ------------------------------------------------------------------ */
  /* 1. Transaction Loop                                                 */
  /* ------------------------------------------------------------------ */
  async function createTransaction(bundleId, body) {
    const res = await fetch(`${LOOP_API}/${LOOP_API_VERSION}/bundle/${bundleId}/transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    log('Transaction :', json);

    const txnId = json && json.data && json.data.txnId;
    if (!res.ok || !txnId) throw new Error(json.message || 'Création de la transaction Loop impossible');
    return txnId;
  }

  /* ------------------------------------------------------------------ */
  /* Calcul du rabais à partir des lignes réellement ajoutées            */
  /* ------------------------------------------------------------------ */
  function computeDiscount(discount, cart, txnId, bundleQty) {
    const key = TXN_PROPERTY_KEYS[0];
    const subtotal = cart.items
      .filter((item) => item.properties && item.properties[key] === txnId)
      .reduce((sum, item) => sum + item.original_line_price, 0) / 100; // prix après selling plan, avant rabais

    const value = Number(discount.value) || 0;
    let computed = 0;

    switch (discount.type) {
      case 'PERCENTAGE':
        computed = subtotal * (value / 100);
        break;
      case 'FIXED_PRICE': // le bundle coûte "value" au total
        computed = Math.max(subtotal - value * bundleQty, 0);
        break;
      case 'FIXED':
      default:
        computed = value * bundleQty;
    }

    return {
      discountType: discount.type,
      discountValue: value,
      discountComputedValue: Math.round(computed * 100) / 100,
    };
  }

  // Garde seulement les rabais des bundles encore présents dans le panier
  function cleanDiscountAttributes(cart) {
    let existing = {};
    try {
      existing = JSON.parse((cart.attributes && cart.attributes._loopBundleDiscountAttributes) || '{}');
    } catch (e) { /* attribut invalide, on repart à zéro */ }

    const idsInCart = new Set(
      cart.items
        .map((i) => i.properties && (i.properties._bundleId || i.properties._loopBundleTxnId))
        .filter(Boolean)
    );

    return Object.fromEntries(Object.entries(existing).filter(([id]) => idsInCart.has(id)));
  }

  /* ------------------------------------------------------------------ */
  /* Fonction principale                                                 */
  /* ------------------------------------------------------------------ */
  /**
   * @param {Object}   o
   * @param {string}   o.bundleId        ID du bundle dans Loop
   * @param {Array}    o.variants        [{ id: variantId, quantity }] pour UN bundle
   * @param {number}   [o.sellingPlanId] Selling plan Shopify. null/absent = achat unique
   * @param {number}   [o.quantity=1]    Nombre de bundles
   * @param {string}   [o.bundleName]    Nom affiché (défaut : nom Loop)
   * @param {string}   [o.boxSizeId]     Défaut : box size par défaut du bundle
   * @param {string}   [o.discountId]    Défaut : rabais Loop selon le type d'achat
   */
  async function addPresetBundle(o) {
    const bundleQty = Number(o.quantity || 1);
    const sellingPlanId = o.sellingPlanId ? Number(o.sellingPlanId) : null;
    const isSubscription = Boolean(sellingPlanId);

    if (!o.bundleId) throw new Error('bundleId manquant');
    if (!Array.isArray(o.variants) || !o.variants.length) throw new Error('variants manquant');

    const details = await getBundleDetails(o.bundleId);

    const boxSize =
      (details.boxSizes || []).find((b) => b.id === o.boxSizeId) ||
      (details.boxSizes || []).find((b) => b.defaultSelected) ||
      (details.boxSizes || [])[0];

    const discount = o.discountId
      ? (details.discounts || []).find((d) => d.id === o.discountId)
      : pickDiscount(details, isSubscription);

    // 1. Transaction
    const body = {
      boxSizeId: boxSize ? boxSize.id : undefined,
      sellingPlanShopifyId: sellingPlanId,
      quantity: bundleQty,
      productVariants: o.variants.map((v) => ({
        shopifyId: Number(v.id),
        quantity: Number(v.quantity || 1),
      })),
    };
    if (discount) body.discountId = discount.id;

    const txnId = await createTransaction(o.bundleId, body);

    // 2. Ajout au panier
    const bundleName = o.bundleName || details.name || 'Bundle';
    const items = o.variants.map((v) => {
      const properties = { _isPresetBundleProduct: true, _bundleName: bundleName };
      TXN_PROPERTY_KEYS.forEach((key) => { properties[key] = txnId; });

      const item = {
        id: Number(v.id),
        quantity: Number(v.quantity || 1) * bundleQty,
        properties,
      };
      if (sellingPlanId) item.selling_plan = sellingPlanId;
      return item;
    });

    log('Ajout au panier :', items);

    const addRes = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const addJson = await addRes.json();
    if (!addRes.ok) throw new Error(addJson.description || addJson.message || 'Erreur /cart/add.js');

    // 3. Attribut de rabais
    let cart = await (await fetch(`${root()}cart.js`)).json();

    if (discount) {
      const attributes = {
        ...cleanDiscountAttributes(cart),
        [txnId]: computeDiscount(discount, cart, txnId, bundleQty),
      };
      log('Attribut de rabais :', attributes);

      const updateRes = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: { _loopBundleDiscountAttributes: JSON.stringify(attributes) },
        }),
      });
      cart = await updateRes.json();
    } else {
      console.warn('[LoopBundle] Aucun rabais Loop trouvé pour ce type d\'achat');
    }

    log('Panier final :', cart);

    document.dispatchEvent(new CustomEvent('loop:preset-bundle:added', {
      detail: { cart, txnId, bundleId: o.bundleId, sellingPlanId },
    }));

    return { cart, txnId };
  }

  /* ------------------------------------------------------------------ */
  /* Boutons : [data-loop-bundle-add]                                     */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-loop-bundle-add]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;

    // Abonnement ou non : data-selling-plan-id fixe, ou radio coché via data-purchase-input
    let sellingPlanId = button.dataset.sellingPlanId || null;
    if (button.dataset.purchaseInput) {
      const checked = document.querySelector(`${button.dataset.purchaseInput}:checked`);
      sellingPlanId = checked && checked.value ? checked.value : null;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      await addPresetBundle({
        bundleId: button.dataset.bundleId,
        bundleName: button.dataset.bundleName,
        variants: JSON.parse(button.dataset.variants || '[]'),
        quantity: button.dataset.quantity || 1,
        sellingPlanId,
      });
      if (button.dataset.redirect) window.location.href = button.dataset.redirect;
    } catch (error) {
      console.error('[LoopBundle]', error);
      alert("Le bundle n'a pas pu être ajouté au panier. Réessayez dans un instant.");
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });

  window.LoopPresetBundle = { add: addPresetBundle, getDetails: getBundleDetails };
})();
